<script lang="ts" setup>
import EditTravelOrderDialog from "@/views/trips/edit/components/dialogs/EditTravelOrderDialog.vue";
import TravelOrderStatusLabel from "@/views/trips/components/TravelOrderStatus.vue";
import { useValidation } from "@/composables/useValidation";
import type { TravelOrderDto, TravelOrderPayment } from "@/api/trips/dto/trip.dto";
import type { TruckDto } from "@/api/trucks/dto/truck.dto";
import { computed, ref } from "vue";
import { TravelOrderCancellationStatusMap } from "@/constants/trip";
import { useI18n } from "vue-i18n";
import { TravelOrderPaymentStatus, TravelOrderPaymentType } from "@/constants/travelOrder";
import TravelOrderPaymentList from "@/views/trips/components/TravelOrderPaymentList.vue";
import type { Trip, TripRoute, TripRouteStop } from "@/types/trip";

const { requiredValidator } = useValidation();
const { t } = useI18n();

const travelOrder = defineModel<TravelOrderDto>();

const props = defineProps<{
  route: TripRoute;
  truck: TruckDto;
  tripFromApi: Trip | null;
  travelOrdersByOrder: Record<string, TravelOrderDto[]>;
  routeStops: TripRouteStop[];
}>();

defineEmits<{
  (e: "update:modelValue", value: TravelOrderDto): void;
}>();

const isEditDialog = ref<boolean>(false);

const relatedRouteStops = computed(() => {
  return props.routeStops.filter((stop) => stop?.order?.id === travelOrder.value?.orderId);
});

const travelOrderStatuses = computed(() => {
  return TravelOrderCancellationStatusMap;
});

const isCancelled = computed(() => {
  return travelOrderStatuses.value.some((s) => s.id === travelOrder.value?.status);
});

const flatLinehaulAmount = computed(() => {
  return travelOrder.value?.payments.find((payment) => payment.type === TravelOrderPaymentType.FlatLinehaul)?.amount;
});

const isOnlyFlatLinehaul = computed(() => {
  return (
    travelOrder.value?.payments.find((payment) => payment.type === TravelOrderPaymentType.FlatLinehaul) &&
    travelOrder.value?.payments.length === 1
  );
});

const travelOrderFromAPI = computed(() => {
  const travelOrders = props.tripFromApi?.routes.flatMap((route) => route.travelOrders);

  return travelOrders?.find((to) => to.id === travelOrder.value?.id) || null;
});

function updateFlatLinehaulAmount(amount: string) {
  if (!travelOrder.value) {
    return;
  }

  let payments: TravelOrderPayment[] = travelOrder.value?.payments || [];

  const hasFlatLinehaulPayment = payments.some((payment) => payment.type === TravelOrderPaymentType.FlatLinehaul);

  if (hasFlatLinehaulPayment) {
    payments = payments.map((payment) => {
      if (payment.type === TravelOrderPaymentType.FlatLinehaul) {
        return {
          ...payment,
          amount: amount ? Number(amount) : null,
        };
      }

      return payment;
    });
  } else {
    payments.push({
      amount: Number(amount),
      type: TravelOrderPaymentType.FlatLinehaul,
      status: TravelOrderPaymentStatus.New,
    });
  }

  travelOrder.value.payments = payments;
}
</script>

<template>
  <div v-if="travelOrder" class="trip-block" data-qa="travel-order-block">
    <div :class="isCancelled ? 'bg-red-lighten-5' : 'bg-white'" class="trip-block__inner pa-4 rounded-lg">
      <div class="trip-block__header d-flex align-center justify-space-between mb-3">
        <div class="d-flex align-center">
          <span class="text-grey-darken-2 font-weight-medium" data-qa="travel-order-name">{{ travelOrder.name }}</span>
          <travel-order-status-label
            :travel-order="travelOrder"
            class="ml-2"
            data-qa="travel-order-status"
          ></travel-order-status-label>
        </div>

        <v-btn
          class="px-2 py-1 font-weight-bold"
          color="primary"
          prepend-icon="mdi-pencil-outline"
          size="small"
          variant="outlined"
          @click="isEditDialog = true"
        >
          {{ t("general.edit-details") }}
        </v-btn>
      </div>

      <div>
        <v-text-field
          v-if="isOnlyFlatLinehaul"
          :label="t('payments.flat-linehaul')"
          :model-value="flatLinehaulAmount"
          :rules="[requiredValidator]"
          class="required"
          color="primary"
          data-qa="driver-rate"
          density="compact"
          hide-details="auto"
          type="number"
          variant="outlined"
          @update:model-value="updateFlatLinehaulAmount"
        >
          <template #append-inner>
            <span class="font-size-16 text-grey-darken-1">$</span>
          </template>
        </v-text-field>

        <travel-order-payment-list
          v-else
          :is-cancelled="isCancelled"
          :payments="travelOrder.payments"
          :travel-order="travelOrder"
        />
      </div>
    </div>

    <edit-travel-order-dialog
      v-if="isEditDialog"
      v-model="isEditDialog"
      v-model:travel-order="travelOrder"
      :route="route"
      :route-stops="relatedRouteStops"
      :travel-order-from-api="travelOrderFromAPI"
      :travel-orders-by-order="travelOrdersByOrder"
      :truck="truck"
    />
  </div>
</template>

<style scoped>
.trip-block__inner {
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
